import { format, parseISO } from "date-fns"
import { Loader2 } from "lucide-react"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../components/ui/table"
import { exactCurrencyFormatter } from "../../../../utils"
import { PromotionLine } from "../../promo_creation/types"

interface PromotionLinesTableProps {
  lines: PromotionLine[]
  loading?: boolean
  error?: string | null
  className?: string
}

/** Coerce a money value (JSON number, or string for safety) to a finite number or null. */
function to_number(value?: number | string | null): number | null {
  if (value == null || value === "") return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function money_cell(value?: number | string | null): string {
  const number = to_number(value)
  return number == null ? "—" : exactCurrencyFormatter(number)
}

function sum_field(lines: PromotionLine[], field: keyof PromotionLine): number {
  return lines.reduce((total, line) => total + (to_number(line[field] as number | string | null | undefined) ?? 0), 0)
}

/**
 * The line's per-unit billback rate(s) from the imported detail (e.g. "scan $1.30"),
 * plus the contract charge percentage when present. Imported lines rarely set the
 * shopper-facing discount columns — this is usually the only real deal term.
 */
function unit_rate_cell(line: PromotionLine): string {
  const parts = (line.unit_rates ?? [])
    .filter(rate => Number.isFinite(rate.amount) && rate.amount > 0)
    .map(rate => `${rate.kind} $${rate.amount.toFixed(2)}`)
  const pct = to_number(line.charge_rate_pct)
  if (pct != null && pct > 0) parts.push(`${+(pct * 100).toFixed(2)}%`)
  return parts.length ? parts.join(", ") : "—"
}

function short_date(iso: string): string {
  return format(parseISO(iso), "MMM d, yyyy")
}

/** True once any line carries a planned volume (the form's per-product rows do; imports rarely). */
export function has_line_volumes(lines: PromotionLine[]): boolean {
  return lines.some(line => to_number(line.planned_volume_units) != null || to_number(line.planned_volume_cases) != null)
}

function volume_cell(line: PromotionLine): string {
  const units = to_number(line.planned_volume_units)
  const cases = to_number(line.planned_volume_cases)
  const parts: string[] = []
  if (units != null) parts.push(`${Math.round(units).toLocaleString("en-US")} units`)
  if (cases != null) parts.push(`${cases.toLocaleString("en-US")} cases`)
  return parts.length ? parts.join(" · ") : "—"
}

/** True once any line states a window of its own — only then is the Runs column worth a column. */
export function has_line_periods(lines: PromotionLine[]): boolean {
  return lines.some(line => line.period != null)
}

/**
 * The window a tactic actually runs in.
 *
 * A promotion imported from a planner grid names a *month*, and that month is not the calendar
 * month: an off-invoice allowance comes off the invoice on the distributor's buy dates, weeks
 * before the scan beside it is earned at retail. Naming the source is what makes the dates
 * explicable — "UNFI buy dates" rather than an unexplained window starting in November.
 */
function LineWindowCell({ line }: { line: PromotionLine }) {
  if (!line.period) {
    return <span class="text-gray-500">—</span>
  }
  const { start, end, source_label, published } = line.period
  return (
    <div>
      <div class="whitespace-nowrap text-gray-900">
        {short_date(start)} – {short_date(end)}
      </div>
      {source_label ? (
        <div class={`text-xs ${published ? "text-emerald-700" : "text-gray-500"}`}>{source_label}</div>
      ) : null}
    </div>
  )
}

/**
 * Shared table of a promotion's per-product line items (from external TPM imports like Promomash).
 * Rendered both as the modal "Lines" tab and as a section in the Edit tab.
 */
export function PromotionLinesTable({ lines, loading, error, className }: PromotionLinesTableProps) {
  if (loading) {
    return (
      <div class={`flex items-center justify-center py-12 text-sm text-gray-600 ${className ?? ""}`}>
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading line items...
      </div>
    )
  }

  if (error) {
    return (
      <div class={`rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800 ${className ?? ""}`}>{error}</div>
    )
  }

  if (lines.length === 0) {
    return (
      <div class={`rounded-md border border-gray-200 p-6 text-center text-sm text-gray-500 ${className ?? ""}`}>
        No line items imported for this promotion.
      </div>
    )
  }

  const total_planned = sum_field(lines, "total_planned_spending")
  const total_actual = sum_field(lines, "actual_spending")
  const show_windows = has_line_periods(lines)
  const show_volumes = has_line_volumes(lines)
  const leading_columns = 9 + (show_windows ? 1 : 0) + (show_volumes ? 1 : 0)

  return (
    <div class={`space-y-2 ${className ?? ""}`}>
      {show_windows && (
        <p class="text-xs text-gray-600">
          Tactics on one promotion do not always run in the same window. An off-invoice allowance comes off the invoice
          on the distributor&apos;s <strong>buy</strong> dates, while a scan, MCB or ad beside it is earned at retail
          during the published <strong>promo</strong> dates — so a planner&apos;s &ldquo;January&rdquo; is not the
          calendar month, and the promotion&apos;s own dates are the union of its lines. Each window below names where
          it came from; <span class="text-gray-500">grey</span> means no published calendar covered that month and the
          whole month is used.
        </p>
      )}
      <div class="overflow-auto rounded-lg border border-gray-200 shadow-sm">
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-50">
            <TableHead className="pl-6 font-semibold text-gray-700">Product</TableHead>
            <TableHead className="font-semibold text-gray-700">Tactic</TableHead>
            {show_windows && <TableHead className="font-semibold text-gray-700">Runs</TableHead>}
            <TableHead className="font-semibold text-gray-700">Deal</TableHead>
            <TableHead className="font-semibold text-gray-700">Supplier</TableHead>
            <TableHead className="text-right font-semibold text-gray-700">Reg price</TableHead>
            <TableHead className="text-right font-semibold text-gray-700">Promo price</TableHead>
            <TableHead className="text-right font-semibold text-gray-700">Unit rate</TableHead>
            <TableHead className="text-right font-semibold text-gray-700">Discount</TableHead>
            {show_volumes && <TableHead className="text-right font-semibold text-gray-700">Planned volume</TableHead>}
            <TableHead className="text-right font-semibold text-gray-700">Allowance</TableHead>
            <TableHead className="text-right font-semibold text-gray-700">Planned spend</TableHead>
            <TableHead className="text-right font-semibold text-gray-700">Actual spend</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {lines.map((line) => (
            <TableRow key={line.id} className="hover:bg-gray-50">
              <TableCell className="py-3 pl-6">
                <div class="font-medium text-gray-900">{line.product_name || "—"}</div>
                {line.upc ? <div class="text-xs text-gray-500">UPC {line.upc}</div> : null}
              </TableCell>
              <TableCell className="py-3 text-gray-700">{line.tactic || "—"}</TableCell>
              {show_windows && (
                <TableCell className="py-3 text-gray-700">
                  <LineWindowCell line={line} />
                </TableCell>
              )}
              <TableCell className="py-3 text-gray-700">{line.deal || "—"}</TableCell>
              <TableCell className="py-3 text-gray-700">{line.supplier || "—"}</TableCell>
              <TableCell className="py-3 text-right text-gray-900">{money_cell(line.regular_shopper_price)}</TableCell>
              <TableCell className="py-3 text-right text-gray-900">{money_cell(line.promoted_shopper_price)}</TableCell>
              <TableCell className="py-3 text-right text-gray-900">{unit_rate_cell(line)}</TableCell>
              <TableCell className="py-3 text-right text-gray-900">{money_cell(line.discount_amount)}</TableCell>
              {show_volumes && <TableCell className="py-3 text-right text-gray-900">{volume_cell(line)}</TableCell>}
              <TableCell className="py-3 text-right text-gray-900">{money_cell(line.total_allowance)}</TableCell>
              <TableCell className="py-3 text-right font-medium text-gray-900">{money_cell(line.total_planned_spending)}</TableCell>
              <TableCell className="py-3 text-right text-gray-900">{money_cell(line.actual_spending)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
        <tfoot>
          <TableRow className="border-t border-gray-200 bg-gray-50 font-semibold">
            <TableCell className="py-3 pl-6 text-gray-900" colSpan={leading_columns}>
              Total ({lines.length} line{lines.length !== 1 ? "s" : ""})
            </TableCell>
            <TableCell className="py-3 text-right text-gray-900">{exactCurrencyFormatter(total_planned)}</TableCell>
            <TableCell className="py-3 text-right text-gray-900">{exactCurrencyFormatter(total_actual)}</TableCell>
          </TableRow>
        </tfoot>
      </Table>
      </div>
    </div>
  )
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  describe("PromotionLinesTable helpers", () => {
    it("parses money values (numbers, and strings for safety)", () => {
      expect(to_number(406.62)).toBe(406.62)
      expect(to_number(0)).toBe(0)
      expect(to_number("406.62")).toBe(406.62)
    })

    it("treats null/empty/invalid as no value", () => {
      expect(to_number(null)).toBeNull()
      expect(to_number(undefined)).toBeNull()
      expect(to_number("")).toBeNull()
      expect(to_number("n/a")).toBeNull()
    })

    it("renders an em dash for missing money and currency for present values", () => {
      expect(money_cell(null)).toBe("—")
      expect(money_cell(1234.5)).toBe(exactCurrencyFormatter(1234.5))
    })

    it("sums a numeric field across lines, ignoring nulls", () => {
      const lines = [
        { id: "1", promotion_id: "p", total_planned_spending: 100.5 },
        { id: "2", promotion_id: "p", total_planned_spending: null },
        { id: "3", promotion_id: "p", total_planned_spending: 9.5 },
      ] as PromotionLine[]
      expect(sum_field(lines, "total_planned_spending")).toBe(110)
    })

    it("renders the per-unit rate(s) and charge percentage, dash when absent", () => {
      const base = { id: "1", promotion_id: "p" } as PromotionLine
      expect(unit_rate_cell({ ...base, unit_rates: [{ kind: "scan", amount: 1.3 }] })).toBe("scan $1.30")
      expect(
        unit_rate_cell({ ...base, unit_rates: [{ kind: "billback", amount: 0.42 }], charge_rate_pct: 0.1014 })
      ).toBe("billback $0.42, 10.14%")
      expect(unit_rate_cell({ ...base, unit_rates: [{ kind: "scan", amount: 0 }] })).toBe("—")
      expect(unit_rate_cell(base)).toBe("—")
    })

    it("shows the Runs column only when a line states a window of its own", () => {
      const base = { id: "1", promotion_id: "p" } as PromotionLine
      expect(has_line_periods([base])).toBe(false)
      expect(
        has_line_periods([
          base,
          {
            ...base,
            id: "2",
            period: { start: "2025-11-30", end: "2026-01-10", source: "unfi-buy", source_label: "UNFI buy dates", published: true },
          },
        ])
      ).toBe(true)
    })

    it("formats a window without shifting it across a timezone boundary", () => {
      // parseISO keeps a date-only string local, so a window opening on Nov 30 does not render Nov 29.
      expect(short_date("2025-11-30")).toBe("Nov 30, 2025")
      expect(short_date("2026-01-10")).toBe("Jan 10, 2026")
    })
  })
}
